import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@ascleo/ui/lib/utils";

/**
 * Title sizes map to specific text sizes for different viewport presets:
 * - sm: text-2xl (small titles, subsections)
 * - default: text-3xl (tablet portrait)
 * - lg: text-4xl (tablet landscape, iPad Pro)
 * - xl: text-5xl (large tablet, touchscreen PC, 4K)
 */
export type TitleSize = "sm" | "default" | "lg" | "xl";

const titleVariants = cva("font-bold", {
  variants: {
    size: {
      sm: "text-2xl",
      default: "text-3xl",
      lg: "text-4xl",
      xl: "text-5xl",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
    intent: {
      default: "text-foreground",
      primary: "text-primary",
      error: "text-destructive",
      muted: "text-muted-foreground",
    },
  },
  defaultVariants: {
    size: "lg",
    align: "center",
    intent: "default",
  },
});

export interface TitleProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof titleVariants> {
  /**
   * The heading level to render. Defaults to "h1".
   */
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  children: React.ReactNode;
}

/**
 * Title component for page and section headings.
 * Use for primary headings, page titles, and major section titles.
 *
 * @example
 * ```tsx
 * <Title>Willkommen</Title>
 * <Title size="sm" as="h2">Abschnitt</Title>
 * <Title intent="error">Notfall</Title>
 * ```
 */
const Title = React.forwardRef<HTMLHeadingElement, TitleProps>(
  ({ className, as: Component = "h1", size, align, intent, children, ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn(titleVariants({ size, align, intent }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Title.displayName = "Title";

export { Title };
